import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface SLDDocument {
  id: string;
  document_type: string;
  document_category: string;
  title: string;
  filename: string;
  revision: string | null;
  revision_date: string | null;
  revision_notes: string | null;
  is_current: boolean;
  uploaded_at: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  area_id: string | null;
  notes: string | null;
}

interface DocumentGroup {
  title: string;
  current: SLDDocument | null;
  archive: SLDDocument[];
}

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (d: string | null): string => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const groupDocuments = (docs: SLDDocument[]): DocumentGroup[] => {
  const map: Record<string, SLDDocument[]> = {};
  docs.forEach((d) => {
    if (!map[d.title]) map[d.title] = [];
    map[d.title].push(d);
  });
  return Object.entries(map).map(([title, items]) => {
    items.sort((a, b) => (b.revision_date || '').localeCompare(a.revision_date || ''));
    const current = items.find((i) => i.is_current) || null;
    const archive = items.filter((i) => !i.is_current);
    return { title, current, archive };
  });
};

const DocumentsLibrary: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<SLDDocument[]>([]);
  const [siteName, setSiteName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedArchives, setExpandedArchives] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!siteId) return;
    const load = async () => {
      try {
        const [siteRes, docsRes] = await Promise.all([
          api.get(`/api/sites/${siteId}`),
          api.get(`/api/sites/${siteId}/sld-documents`),
        ]);
        setSiteName(siteRes.data.name);
        setDocuments(docsRes.data || []);
      } catch {
        setError('Failed to load documents.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [siteId]);

  const skmDocs = documents.filter((d) => d.document_category === 'skm_model');
  const sldDocs = documents.filter((d) => d.document_category === 'hv_sld');
  const otherDocs = documents.filter((d) => d.document_category !== 'skm_model' && d.document_category !== 'hv_sld');

  const skmGroups = groupDocuments(skmDocs);
  const sldGroups = groupDocuments(sldDocs);
  const otherGroups = groupDocuments(otherDocs);

  const toggleArchive = (key: string) => {
    setExpandedArchives((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDownload = (doc: SLDDocument) => {
    const token = localStorage.getItem('prot_token');
    const link = document.createElement('a');
    link.href = `/api/documents/${doc.id}/download`;
    if (token) {
      // Use fetch with auth header for download
      fetch(`/api/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.blob())
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          link.href = url;
          link.download = doc.filename;
          link.click();
          URL.revokeObjectURL(url);
        });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f6fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <header style={{
        background: '#1a1a2e',
        color: '#fff',
        padding: '0 32px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate(`/site/${siteId}`)}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            padding: '7px 16px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'inherit',
          }}
        >
          ← Site Overview
        </button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{siteName || 'Documents'}</div>
          <div style={{ fontSize: 11, color: '#8899aa', letterSpacing: 1 }}>📁 SKM MODELS & HV SINGLE LINE DIAGRAMS</div>
        </div>
      </header>

      <main style={{ flex: 1, padding: 32, maxWidth: 1200, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        {loading && <div style={{ textAlign: 'center', color: '#888', padding: 60 }}>Loading documents...</div>}
        {error && (
          <div style={{
            background: '#fff5f5', border: '1px solid #f44336', borderRadius: 8,
            color: '#f44336', padding: '14px 18px', marginBottom: 24,
          }}>
            {error}
          </div>
        )}

        {!loading && documents.length === 0 && (
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: '60px 40px',
            textAlign: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📁</div>
            <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>No documents uploaded yet</div>
            <div style={{ fontSize: 13, color: '#999' }}>SKM PTW models and HV SLDs will appear here once uploaded.</div>
          </div>
        )}

        {!loading && (
          <>
            {/* SKM PTW Models Section */}
            <DocumentCategorySection
              title="SKM PTW Models"
              subtitle="Power system modelling files — SKM Power Tools for Windows"
              emoji="🖥️"
              accentColor="#0f3460"
              groups={skmGroups}
              expandedArchives={expandedArchives}
              onToggleArchive={toggleArchive}
              onDownload={handleDownload}
              categoryKey="skm"
            />

            {/* HV Single Line Diagrams Section */}
            <DocumentCategorySection
              title="HV Single Line Diagrams"
              subtitle="As-built and design SLDs — PDF and DWG formats"
              emoji="⚡"
              accentColor="#e65100"
              groups={sldGroups}
              expandedArchives={expandedArchives}
              onToggleArchive={toggleArchive}
              onDownload={handleDownload}
              categoryKey="sld"
            />

            {/* Other Documents */}
            {otherGroups.length > 0 && (
              <DocumentCategorySection
                title="Other Documents"
                subtitle="Additional reference documents"
                emoji="📄"
                accentColor="#555"
                groups={otherGroups}
                expandedArchives={expandedArchives}
                onToggleArchive={toggleArchive}
                onDownload={handleDownload}
                categoryKey="other"
              />
            )}
          </>
        )}
      </main>

      <footer style={{
        background: '#1a1a2e',
        color: '#8899aa',
        textAlign: 'center',
        padding: '14px',
        fontSize: 12,
        flexShrink: 0,
      }}>
        © 2026 JMB Engineering Pty Ltd — Protection Platform
      </footer>
    </div>
  );
};

/* ──────────── Category Section Component ──────────── */

interface CategorySectionProps {
  title: string;
  subtitle: string;
  emoji: string;
  accentColor: string;
  groups: DocumentGroup[];
  expandedArchives: Record<string, boolean>;
  onToggleArchive: (key: string) => void;
  onDownload: (doc: SLDDocument) => void;
  categoryKey: string;
}

const DocumentCategorySection: React.FC<CategorySectionProps> = ({
  title, subtitle, emoji, accentColor, groups, expandedArchives, onToggleArchive, onDownload, categoryKey,
}) => {
  if (groups.length === 0) return null;

  return (
    <section style={{ marginBottom: 40 }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 20,
        paddingBottom: 12,
        borderBottom: `3px solid ${accentColor}`,
      }}>
        <span style={{ fontSize: 28 }}>{emoji}</span>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{title}</h2>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{subtitle}</div>
        </div>
        <span style={{
          marginLeft: 'auto',
          background: `${accentColor}15`,
          color: accentColor,
          border: `1px solid ${accentColor}33`,
          borderRadius: 20,
          padding: '4px 14px',
          fontSize: 12,
          fontWeight: 600,
        }}>
          {groups.reduce((sum, g) => sum + 1 + g.archive.length, 0)} documents
        </span>
      </div>

      {/* Document Groups */}
      {groups.map((group, gi) => {
        const archiveKey = `${categoryKey}-${gi}`;
        const isExpanded = expandedArchives[archiveKey] || false;

        return (
          <div key={gi} style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            marginBottom: 20,
            overflow: 'hidden',
          }}>
            {/* Group Title Bar */}
            <div style={{
              background: accentColor,
              color: '#fff',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{group.title}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {group.current && (
                  <span style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: 20,
                    padding: '3px 12px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}>
                    Current: {group.current.revision || '—'}
                  </span>
                )}
                {group.archive.length > 0 && (
                  <span style={{
                    background: 'rgba(255,255,255,0.15)',
                    borderRadius: 20,
                    padding: '3px 10px',
                    fontSize: 11,
                  }}>
                    {group.archive.length} archived
                  </span>
                )}
              </div>
            </div>

            {/* Current Document Card */}
            {group.current && (
              <DocumentCard
                doc={group.current}
                isCurrent={true}
                accentColor={accentColor}
                onDownload={onDownload}
              />
            )}

            {/* Archive Section */}
            {group.archive.length > 0 && (
              <>
                <button
                  onClick={() => onToggleArchive(archiveKey)}
                  style={{
                    width: '100%',
                    background: '#f5f6fa',
                    border: 'none',
                    borderTop: '1px solid #e8eaf0',
                    borderBottom: isExpanded ? '1px solid #e8eaf0' : 'none',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#666',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    display: 'inline-block',
                  }}>▶</span>
                  Archive History ({group.archive.length} revision{group.archive.length !== 1 ? 's' : ''})
                </button>

                {isExpanded && group.archive.map((doc, di) => (
                  <DocumentCard
                    key={di}
                    doc={doc}
                    isCurrent={false}
                    accentColor={accentColor}
                    onDownload={onDownload}
                  />
                ))}
              </>
            )}
          </div>
        );
      })}
    </section>
  );
};

/* ──────────── Document Card Component ──────────── */

interface DocumentCardProps {
  doc: SLDDocument;
  isCurrent: boolean;
  accentColor: string;
  onDownload: (doc: SLDDocument) => void;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ doc, isCurrent, accentColor, onDownload }) => (
  <div style={{
    padding: '18px 20px',
    borderBottom: '1px solid #f0f0f0',
    background: isCurrent ? '#fff' : '#fafbfc',
    display: 'flex',
    gap: 20,
    alignItems: 'flex-start',
  }}>
    {/* Left: Status indicator */}
    <div style={{
      width: 4,
      minHeight: 60,
      borderRadius: 2,
      background: isCurrent ? '#4caf50' : '#ddd',
      flexShrink: 0,
      marginTop: 2,
    }} />

    {/* Middle: Document info */}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        {/* Revision badge */}
        <span style={{
          background: isCurrent ? `${accentColor}15` : '#f0f0f0',
          color: isCurrent ? accentColor : '#888',
          border: `1px solid ${isCurrent ? `${accentColor}33` : '#ddd'}`,
          borderRadius: 6,
          padding: '3px 10px',
          fontSize: 13,
          fontWeight: 700,
          fontFamily: 'monospace',
        }}>
          {doc.revision || '—'}
        </span>

        {/* Current/Superseded badge */}
        <span style={{
          background: isCurrent ? '#4caf5015' : '#ff980015',
          color: isCurrent ? '#4caf50' : '#ff9800',
          border: `1px solid ${isCurrent ? '#4caf5033' : '#ff980033'}`,
          borderRadius: 20,
          padding: '2px 10px',
          fontSize: 11,
          fontWeight: 600,
        }}>
          {isCurrent ? '● Current' : 'Superseded'}
        </span>

        {/* Date */}
        <span style={{ fontSize: 12, color: '#888' }}>
          {formatDate(doc.revision_date)}
        </span>
      </div>

      {/* Revision notes */}
      {doc.revision_notes && (
        <div style={{
          fontSize: 13,
          color: '#555',
          lineHeight: 1.5,
          marginBottom: 8,
        }}>
          {doc.revision_notes}
        </div>
      )}

      {/* File info row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{
          fontSize: 12,
          color: '#888',
          fontFamily: 'monospace',
          background: '#f5f6fa',
          padding: '2px 8px',
          borderRadius: 4,
        }}>
          {doc.filename}
        </span>
        <span style={{ fontSize: 11, color: '#aaa' }}>
          {formatFileSize(doc.file_size_bytes)}
        </span>
        {doc.mime_type && (
          <span style={{ fontSize: 11, color: '#aaa' }}>
            {doc.mime_type === 'application/pdf' ? 'PDF' :
             doc.mime_type === 'application/octet-stream' ? 'Binary' :
             doc.mime_type.split('/').pop()?.toUpperCase()}
          </span>
        )}
      </div>
    </div>

    {/* Right: Download button */}
    <button
      onClick={() => onDownload(doc)}
      style={{
        background: isCurrent ? accentColor : '#e8eaf0',
        color: isCurrent ? '#fff' : '#555',
        border: 'none',
        borderRadius: 8,
        padding: '10px 18px',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        if (!isCurrent) {
          (e.currentTarget as HTMLButtonElement).style.background = '#ddd';
        }
      }}
      onMouseLeave={(e) => {
        if (!isCurrent) {
          (e.currentTarget as HTMLButtonElement).style.background = '#e8eaf0';
        }
      }}
    >
      ⬇ Download
    </button>
  </div>
);

export default DocumentsLibrary;
